// Help and training (18 §18.4): a person's own training records, everyone's for the managers'
// Settings → Training, and sending `recordWalkthrough` through the command writer (so it is queued
// offline and synced later, like every other command). `staffTraining/{uid}` is written by the
// server only. The managers' list needs no composite index (`training.indexes.test.ts`).
import { collection, doc, limit, query, type Firestore } from 'firebase/firestore';
import type { TrainingRecords, WalkthroughMode, WalkthroughOutcome } from '@ecobox/domain';
import { staffTrainingSchema, walkthroughRecordSchema } from '@ecobox/schema';
import type { CommandWriter, SendResult } from './commands';
import { STAFF_LIST_LIMIT } from './config';
import { toDoc, watchOne, watchRows, type Unsubscribe } from './docs';

export interface StaffTrainingRow {
  uid: string;
  records: TrainingRecords;
  /** ISO time of the last record, or null when unknown. */
  updatedAt: string | null;
}

function toRow(uid: string, data: Record<string, unknown>): StaffTrainingRow {
  const parsed = staffTrainingSchema.safeParse(toDoc(uid, data));
  // A record that does not parse is left out rather than hiding the person's whole training.
  if (parsed.success)
    return { uid, records: parsed.data.records, updatedAt: parsed.data.updatedAt };
  const raw = (data['records'] ?? {}) as Record<string, unknown>;
  const records: Record<string, TrainingRecords[string]> = {};
  for (const [id, value] of Object.entries(raw)) {
    const one = walkthroughRecordSchema.safeParse(value);
    if (one.success) records[id] = one.data;
  }
  return {
    uid,
    records,
    updatedAt: typeof data['updatedAt'] === 'string' ? data['updatedAt'] : null,
  };
}

/** The signed-in person's records. `{}` when they have taken nothing yet (no document). */
export function watchMyTraining(
  db: Firestore,
  uid: string,
  onData: (records: TrainingRecords) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(
    doc(db, 'staffTraining', uid),
    (id, data) => toRow(id, data),
    (row) => onData(row?.records ?? {}),
    onError,
  );
}

/** Every person's records, for Settings → Training. Needs `users.manage`; at most `max` people. */
export function watchStaffTraining(
  db: Firestore,
  onData: (rows: StaffTrainingRow[]) => void,
  onError: (err: Error) => void,
  max = STAFF_LIST_LIMIT,
): Unsubscribe {
  return watchRows(query(collection(db, 'staffTraining'), limit(max)), toRow, onData, onError);
}

export interface WalkthroughTaken {
  walkthroughId: string;
  version: number;
  outcome: WalkthroughOutcome;
  mode: WalkthroughMode;
}

/**
 * Sends `recordWalkthrough`. Training is never worth an error on screen: when the command cannot
 * be built (no branch chosen yet, signed out) this returns null and logs why.
 */
export function sendWalkthroughRecord(
  writer: Pick<CommandWriter, 'send'>,
  taken: WalkthroughTaken,
): SendResult | null {
  try {
    return writer.send('recordWalkthrough', taken);
  } catch (err) {
    console.warn('The walkthrough could not be recorded.', err);
    return null;
  }
}
